import React, { useState, useEffect } from 'react';
import {
Container,
Typography,
Button,
Paper,
TextField,
Dialog,
DialogTitle,
DialogContent,
DialogActions,
IconButton,
Box,
Chip,
Grid,
Slide,
MenuItem,
InputLabel,
FormControl,
Select,
TableContainer,
Table,
TableHead,
TableRow,
TableCell,
TableBody,
TablePagination,
Divider,
useTheme,
Menu,
Card,
CardContent,
CardHeader,
InputAdornment
} from '@mui/material';
import {
Delete,
Search,
Add as AddIcon,
Close as CloseIcon,
FilterList as FilterIcon,
Refresh as RefreshIcon,
Today as TodayIcon,
Edit as EditIcon,
Visibility as ViewIcon,
PictureAsPdf,
TableChart,
GetApp,
X,
Person,
Hotel,
MeetingRoom,
AttachMoney,
Payments,
Phone,
Email,
LocationOn
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import axios from 'axios';
import {
APIHiringPlaceList,
APIHiringPlaceStore,
APIHiringPlaceUpdate,
APIHiringPlaceDelete,
APIRoomNames,
} from '../../services/APIs';
import { TEXT_CONNECTION_ERROR, TEXT_DATA_LOADED } from '../../services/Text';
import {
formatCreatedAtDate,
formatNumberWithComma,
formatNumberWithComma2,
getCategoriesNameById,
getPaymentMode,
getStatusById,
getUserDetails,
recordsPerPage,
statusData
} from '../../utils/helpers';
import {
exportToCSV,
exportToExcel,
exportToPDF,
} from '../../services/event-management/HiringPlaceService';
import { getUserRole, PERMISSIONS, ROLE_PERMISSIONS } from '../../utils/Permission';
import useCustomToast from '../../hooks/useToast';
import { paymentModes } from '../../utils/dataInfo';

const HiringPlace = () => {
const theme = useTheme();
const [loading, setLoading] = useState(false);
const [error, setError] = useState(null);
const showToast = useCustomToast();
const [updateId, setUpdateId] = useState('');
const [postStatus, setPostStatus] = useState('ADD');
const [exportAnchorEl, setExportAnchorEl] = useState(null);
const [pageState, setPageState] = useState({
    pageData: []
});
const [page, setPage] = useState(0);
const [rowsPerPage, setRowsPerPage] = useState(recordsPerPage);
const [selectedRecord, setSelectedRecord] = useState(null);
const [searchTerm, setSearchTerm] = useState('');

// Dropdown data
const [roomNamesData, setRoomNamesData] = useState([]);
const [roomUnitsOptionNamesData, setRoomUnitsOptionNamesData] = useState([]);

// Form state
const [formData, setFormData] = useState({
    customer_name: '',
    customer_mobile: '',
    customer_email: '',
    customer_address: '',
    room_type_id: '',
    service_name: '',
    cost: '',
    quantity: 1,
    total_amount: '',
    paid_amount: '',
    balance_amount: '',
    payment_method: '',
    description: '',
    status: 1,
    check_in_date: '',
    check_out_date: ''
});

const [filters, setFilters] = useState({
    customer_name: '',
    room_type_id: '',
    service_name: '',
    payment_method: '',
    status: '',
    dateFrom: '',
    dateTo: '',
    check_in_from: '',
    check_in_to: ''
});

const [errors, setErrors] = useState({
    customer_name: false,
    customer_mobile: false,
    room_type_id: false,
    cost: false,
    quantity: false,
    total_amount: false,
    paid_amount: false,
    balance_amount: false,
    payment_method: false
});

// State for dialogs
const [filterOptions, setFilterOptions] = useState(false);
const [openAdd, setOpenAdd] = useState(false);
const [openView, setOpenView] = useState(false);
const [openDelete, setOpenDelete] = useState(false);

// Permissions
const [userRole, setUserRole] = useState('');
useEffect(() => {
    const role = getUserRole();
    setUserRole(role);
}, []);

const hasPermission = (permission) => {
    return ROLE_PERMISSIONS[userRole]?.includes(permission) || false;
};

useEffect(() => {
    fetchData();
    fetchRoomNames();
}, []);

const fetchRoomNames = async () => {
    try {
    const response = await axios.get(APIRoomNames);
    if (response.data.status === 'success') {
        setRoomNamesData(response.data.data || []);
    }
    } catch (error) {
    console.error("Error fetching room names:", error);
    showToast("Error", "Failed to load room names", "error");
    }
};

const fetchData = async () => {
    setLoading(true);
    setError(null);

    try {
    const params = {};

    const [guestRes] = await Promise.all([
        axios.get(APIHiringPlaceList, { params })
    ]);

    const HiringPlaces = Array.isArray(guestRes.data.data) ? guestRes.data.data.reverse() : [];

    setPageState(prev => ({
        ...prev,
        pageData: HiringPlaces
    }));

    showToast("Success", TEXT_DATA_LOADED, "success");
    } catch (error) {
    let errorMessage = "Failed to load data";
    if (error.response) {
        errorMessage = error.response.data.message || error.message;
    } else if (error.code === "ERR_NETWORK") {
        errorMessage = TEXT_CONNECTION_ERROR;
    }
    setError(errorMessage);
    console.log(errorMessage);
    showToast("Error", errorMessage, "error");
    } finally {
    setLoading(false);
    }
};

// Filter based on search term and filters
const filteredData = pageState.pageData.filter(record => {
    const matchesSearch = searchTerm === '' ||
    (record.customer_name && record.customer_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (record.customer_phone && record.customer_phone.includes(searchTerm)) ||
    (record.room_type_name && record.room_type_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (record.service_name && record.service_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
    record.status.toString().includes(searchTerm);

    const matchesCustomer = !filters.customer_name ||
    (record.customer_name && record.customer_name.toLowerCase().includes(filters.customer_name.toLowerCase()));

    const matchesRoomType = !filters.room_type_id ||
    record.room_type_id.toString() === filters.room_type_id.toString();

    const matchesRoomUnit = !filters.service_name ||
    (record.service_name && record.service_name.toString().toLowerCase().includes(filters.service_name.toLowerCase()));

    const matchesPaymentMethod = !filters.payment_method ||
    record.payment_method === filters.payment_method;

    const matchesStatus = !filters.status ||
    record.status.toString().includes(filters.status);

    // Date range filter
    let matchesDate = true;
    if (filters.dateFrom) {
    const recordDate = new Date(record.created_at).toISOString().split('T')[0];
    const filterFromDate = new Date(filters.dateFrom).toISOString().split('T')[0];
    matchesDate = matchesDate && recordDate >= filterFromDate;
    }

    if (filters.dateTo) {
    const recordDate = new Date(record.created_at).toISOString().split('T')[0];
    const filterToDate = new Date(filters.dateTo).toISOString().split('T')[0];
    matchesDate = matchesDate && recordDate <= filterToDate;
    }

    // Check-in date filter
    if (filters.check_in_from && record.check_in_date) {
    const checkInDate = new Date(record.check_in_date).toISOString().split('T')[0];
    const filterFromDate = new Date(filters.check_in_from).toISOString().split('T')[0];
    matchesDate = matchesDate && checkInDate >= filterFromDate;
    }

    if (filters.check_in_to && record.check_in_date) {
    const checkInDate = new Date(record.check_in_date).toISOString().split('T')[0];
    const filterToDate = new Date(filters.check_in_to).toISOString().split('T')[0];
    matchesDate = matchesDate && checkInDate <= filterToDate;
    }

    return matchesSearch && matchesCustomer && matchesRoomType && 
        matchesRoomUnit && matchesPaymentMethod && 
        matchesStatus && matchesDate;
});

// Calculate amounts
const calculateAmounts = (cost, quantity, paid) => {
    const costNum = parseFloat(cost) || 0;
    const quantityNum = parseInt(quantity) || 1;
    const paidNum = parseFloat(paid) || 0;
    
    const total = costNum * quantityNum;
    const balance = total - paidNum;
    
    return {
    total: total.toFixed(2),
    balance: balance.toFixed(2)
    };
};

// Pagination handlers
const handleChangePage = (event, newPage) => {
    setPage(newPage);
};

const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
};

// Dialog handlers
const handleOpenAdd = () => {
    setPostStatus("ADD");
    setFormData({
    customer_name: '',
    customer_mobile: '',
    customer_email: '',
    customer_address: '',
    room_type_id: '',
    service_name: '',
    cost: '',
    quantity: 1,
    total_amount: '',
    paid_amount: '',
    balance_amount: '',
    payment_method: '',
    description: '',
    status: 1,
    check_in_date: '',
    check_out_date: ''
    });
    setErrors({
    customer_name: false,
    customer_mobile: false,
    room_type_id: false,
    cost: false,
    quantity: false,
    total_amount: false,
    paid_amount: false,
    balance_amount: false,
    payment_method: false
    });
    setOpenAdd(true);
};

const handleCloseAdd = () => {
    setOpenAdd(false);
};

const handleOpenView = (record) => {
    setSelectedRecord(record);
    setOpenView(true);
};

const handleCloseView = () => setOpenView(false);

const handleOpenEdit = (record) => {
    setSelectedRecord(record);
    setUpdateId(record.id);
    setPostStatus("UPDATE");

    // Set form data
    setFormData({
    customer_name: record.customer_name || '',
    customer_mobile: record.customer_phone || '',
    customer_email: record.customer_email || '',
    customer_address: record.customer_address || '',
    room_type_id: record.room_type_id || '',
    service_name: record.service_name || '',
    cost: record.cost || '',
    quantity: record.quantity || 1,
    total_amount: record.total_amount || '',
    paid_amount: record.paid_amount || '',
    balance_amount: record.balance_amount || '',
    payment_method: record.payment_method || '',
    description: record.description || '',
    status: record.status || 1,
    check_in_date: record.check_in_date || '',
    check_out_date: record.check_out_date || ''
    });
    setOpenAdd(true);
};

const handleOpenDelete = (record) => {
    setSelectedRecord(record);
    setUpdateId(record.id);
    setPostStatus("DELETE");
    setOpenDelete(true);
};

const handleCloseDelete = () => {
    setOpenDelete(false);
};

// Form handlers
const handleInputChange = (e) => {
    const { name, value } = e.target;

    // Reset error for this field
    setErrors(prev => ({
    ...prev,
    [name]: false
    }));

    let newFormData = { ...formData, [name]: value };

    // Recalculate amounts when cost, quantity, or paid amount changes
    if (name === 'cost' || name === 'quantity' || name === 'paid_amount') {
    const amounts = calculateAmounts(
        name === 'cost' ? value : newFormData.cost,
        name === 'quantity' ? value : newFormData.quantity,
        name === 'paid_amount' ? value : newFormData.paid_amount
    );
    newFormData.total_amount = amounts.total;
    newFormData.balance_amount = amounts.balance;
    }

    setFormData(newFormData);
};

// Filter handlers
const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
    ...prev,
    [name]: value
    }));
};

const applyTodayFilter = () => {
    const today = new Date().toISOString().split('T')[0];
    setFilters({
    ...filters,
    dateFrom: today,
    dateTo: today
    });
    setFilterOptions(false);
};

const clearFilters = () => {
    setFilters({
    customer_name: '',
    room_type_id: '',
    service_name: '',
    payment_method: '',
    status: '',
    dateFrom: '',
    dateTo: '',
    check_in_from: '',
    check_in_to: ''
    });
    setSearchTerm('');
    setFilterOptions(false);
};

// Export handlers
const handleExportClick = (event) => { setExportAnchorEl(event.currentTarget); };
const handleExportClose = () => { setExportAnchorEl(null); };

const handleExportExcel = async () => {
    try {
    handleExportClose();
    await exportToExcel(filteredData, [], roomNamesData, roomUnitsOptionNamesData, [], {
        customer_name: filters.customer_name,
        room_type_id: filters.room_type_id,
        service_name: filters.service_name,
        payment_method: filters.payment_method,
        status: filters.status,
        dateFrom: filters.dateFrom,
        dateTo: filters.dateTo
    });
    showToast("Success", "Excel file downloaded successfully", "success");
    } catch (error) {
    console.error('Excel export error:', error);
    showToast("Error", error.message, "error");
    }
};

const handleExportPDF = async () => {
    try {
    handleExportClose();
    await exportToPDF(filteredData, [], roomNamesData, roomUnitsOptionNamesData, [], {
        customer_name: filters.customer_name,
        room_type_id: filters.room_type_id,
        service_name: filters.service_name,
        payment_method: filters.payment_method,
        status: filters.status,
        dateFrom: filters.dateFrom,
        dateTo: filters.dateTo
    });
    showToast("Success", "PDF file downloaded successfully", "success");
    } catch (error) {
    console.error('PDF export error:', error);
    showToast("Error", error.message, "error");
    }
};

const handleExportCSV = async () => {
    try {
    handleExportClose();
    await exportToCSV(filteredData, [], roomNamesData, roomUnitsOptionNamesData, []);
    showToast("Success", "CSV file downloaded successfully", "success");
    } catch (error) {
    console.error('CSV export error:', error);
    showToast("Error", error.message, "error");
    }
};

const validateForm = () => {
    const newErrors = {};
    
    // Customer validation
    if (!formData.customer_name.trim()) {
    newErrors.customer_name = true;
    }
    if (!formData.customer_mobile.trim()) {
    newErrors.customer_mobile = true;
    }
    
    // Room validation
    if (!formData.room_type_id) {
    newErrors.room_type_id = true;
    }
    
    // Amount validation
    if (!formData.cost || isNaN(formData.cost) || parseFloat(formData.cost) <= 0) {
    newErrors.cost = true;
    }
    if (!formData.quantity || isNaN(formData.quantity) || parseInt(formData.quantity) <= 0) {
    newErrors.quantity = true;
    }
    if (!formData.total_amount || isNaN(formData.total_amount) || parseFloat(formData.total_amount) <= 0) {
    newErrors.total_amount = true;
    }
    if (!formData.paid_amount || isNaN(formData.paid_amount) || parseFloat(formData.paid_amount) < 0) {
    newErrors.paid_amount = true;
    }
    if (!formData.balance_amount || isNaN(formData.balance_amount)) {
    newErrors.balance_amount = true;
    }
    
    // Payment method validation
    if (!formData.payment_method) {
    newErrors.payment_method = true;
    }

    setErrors(newErrors);
    
    if (Object.keys(newErrors).length > 0) {
    const errorMessages = [];
    if (newErrors.customer_name) errorMessages.push('Customer Name is required');
    if (newErrors.customer_mobile) errorMessages.push('Customer Mobile Number is required');
    if (newErrors.room_type_id) errorMessages.push('Room Name is required');
    if (newErrors.cost) errorMessages.push('Valid Cost per unit is required');
    if (newErrors.quantity) errorMessages.push('Valid Quantity is required');
    if (newErrors.total_amount) errorMessages.push('Valid Total Amount is required');
    if (newErrors.paid_amount) errorMessages.push('Valid Paid Amount is required');
    if (newErrors.balance_amount) errorMessages.push('Valid Balance Amount is required');
    if (newErrors.payment_method) errorMessages.push('Payment Method is required');
    
    showToast("Validation Error", errorMessages.join('\n'), "warning");
    return false;
    }
    
    // Validate amount calculations
    const calculatedTotal = parseFloat(formData.cost) * parseInt(formData.quantity);
    const calculatedBalance = parseFloat(formData.total_amount) - parseFloat(formData.paid_amount);
    
    if (Math.abs(calculatedTotal - parseFloat(formData.total_amount)) > 0.01) {
    showToast("Validation Error", `Total amount should be ${formatNumberWithComma(calculatedTotal)} (Cost × Quantity)`, "warning");
    return false;
    }
    
    if (Math.abs(calculatedBalance - parseFloat(formData.balance_amount)) > 0.01) {
    showToast("Validation Error", `Balance amount should be ${formatNumberWithComma(calculatedBalance)} (Total - Paid)`, "warning");
    return false;
    }
    
    return true;
};

const handleDialogClose = () => {
    setOpenAdd(false);
    setOpenView(false);
    setOpenDelete(false);
    setFilterOptions(false);
};

const handleFormDataReset = () => {
    setFormData({
    customer_name: '',
    customer_mobile: '',
    customer_email: '',
    customer_address: '',
    room_type_id: '',
    service_name: '',
    cost: '',
    quantity: 1,
    total_amount: '',
    paid_amount: '',
    balance_amount: '',
    payment_method: '',
    description: '',
    status: 1,
    check_in_date: '',
    check_out_date: ''
    });
};

const handleSubmit = async (e) => {
    e.preventDefault();
    if (postStatus !== "DELETE") {
    if (!validateForm()) return;
    }

    const formDataToSend = new FormData();
    formDataToSend.append('user_id', getUserDetails("Id"));

    // Always use current user's branch ID
    formDataToSend.append('branch_id', getUserDetails("branchId"));

    // Customer data - now all fields are sent directly
    formDataToSend.append('customer_name', formData.customer_name);
    formDataToSend.append('customer_phone', formData.customer_mobile);
    formDataToSend.append('customer_email', formData.customer_email || '');
    formDataToSend.append('customer_address', formData.customer_address || '');
    
    // Room data
    formDataToSend.append('room_type_id', formData.room_type_id);
    formDataToSend.append('service_name', formData.service_name || '');
    
    // Amount data
    formDataToSend.append('cost', formData.cost);
    formDataToSend.append('quantity', formData.quantity);
    formDataToSend.append('total_amount', formData.total_amount);
    formDataToSend.append('paid_amount', formData.paid_amount);
    formDataToSend.append('balance_amount', formData.balance_amount);
    
    // Payment and other data
    formDataToSend.append('payment_method', formData.payment_method);
    formDataToSend.append('description', formData.description || '');
    formDataToSend.append('status', formData.status);
    formDataToSend.append('check_in_date', formData.check_in_date || '');
    formDataToSend.append('check_out_date', formData.check_out_date || '');

    // Only append updateId for UPDATE/DELETE operations
    if (postStatus === "UPDATE" || postStatus === "DELETE") {
    formDataToSend.append('updateId', updateId);
    }
    
    try {
    let apiUrl, method;
    if (postStatus === "ADD") {
        apiUrl = APIHiringPlaceStore;
        method = 'post';
    } else if (postStatus === "UPDATE") {
        apiUrl = APIHiringPlaceUpdate;
        method = 'post';
    } else if (postStatus === "DELETE") {
        apiUrl = APIHiringPlaceDelete;
        method = 'post';
    }

    const response = await axios({
        method,
        url: apiUrl,
        data: formDataToSend
    });

    if (response.data.status === "success") {
        showToast("Success", response.data.message, "success");
        handleFormDataReset();
        handleDialogClose();
        fetchData();
    } else {
        showToast("Warning", response.data.message || "Operation completed with warnings", "warning");
    }
    } catch (error) {
    if (error.response) {
        const { status, data } = error.response;
        if (status === 422) {
        const errors = data.errors || {};
        let errorMessages = [];
        for (const field in errors) {
            errorMessages.push(`${field}: ${errors[field].join(', ')}`);
        }
        showToast(
            "Validation Error",
            errorMessages.join('\n') || "Please correct the form errors",
            "error"
        );
        } else if (status === 409) {
        showToast("Error", data.message || "This record already exists", "error");
        } else if (status >= 500) {
        showToast("Server Error", data.message || "Internal server error", "error");
        } else {
        showToast("Error", data.message || "Request failed", "error");
        }
    } else if (error.request) {
        showToast("Network Error", "No response from server. Please check your connection.", "error");
    } else {
        showToast("Error", error.message || "An unexpected error occurred", "error");
    }
    console.error("API Error:", error);
    }
};

if (loading) return <div>Loading...</div>;
if (error) return <div>Error: {error}</div>;

return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
    <Typography sx={{
        fontWeight: 'bold',
        color: theme.palette.primary.main,
        flexShrink: 0,
        width: { xs: '100%', sm: 'auto' },
        mb: { xs: 1, sm: 0 }
    }}>
        Hiring Place Management ({formatNumberWithComma2(pageState.pageData.length || 0)})
    </Typography>

    <Paper elevation={2} sx={{ p: 3, mb: 3, borderRadius: 3 }}>
        <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: 2, 
        flexWrap: 'wrap' 
        }}>
        {/* Buttons - column on mobile, row on desktop */}
        <Box sx={{ 
            display: 'flex', 
            gap: 1, 
            flexShrink: 0,
            order: { xs: 2, sm: 1 }
        }}>
            {hasPermission(PERMISSIONS.ADD) && (
            <Button
                variant="contained"
                color="warning"
                startIcon={<AddIcon />}
                onClick={handleOpenAdd}
                sx={{
                textTransform: 'none',
                borderRadius: '8px'
                }}
                size="small"
            >
                New Booking
            </Button>
            )}
            
            <Button
            variant="contained"
            color="secondary"
            startIcon={<FilterIcon />}
            onClick={() => setFilterOptions(true)}
            sx={{
                textTransform: 'none',
                borderRadius: '8px'
            }}
            size="small"
            >
            View Options
            </Button>
            
            {hasPermission(PERMISSIONS.EXPORT) && (
            <Button
                variant="contained"
                color="primary"
                startIcon={<GetApp />}
                onClick={handleExportClick}
                sx={{
                textTransform: 'none',
                borderRadius: '8px'
                }}
                size="small"
            >
                Export Data
            </Button>
            )}
            
            <Button
            variant="outlined"
            color="info"
            startIcon={<RefreshIcon />}
            onClick={fetchData}
            sx={{
                textTransform: 'none',
                borderRadius: '8px'
            }}
            size="small"
            >
            Refresh
            </Button>
        </Box>

        {/* Search Bar - full width on mobile, fixed on desktop */}
        <Paper sx={{
            width: { xs: '100%', sm: '300px' },
            p: '2px 8px',
            display: 'flex',
            alignItems: 'center',
            marginLeft: { xs: 0, sm: 'auto' },
            order: { xs: 1, sm: 2 },
            borderRadius: '8px',
            background: theme.palette.primary.light
        }}>
            <TextField
            fullWidth
            variant="outlined"
            placeholder="Search Bookings..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
                startAdornment: <Search sx={{ mr: 1, color: 'white' }} />,
                sx: {
                '& fieldset': { border: 'none' },
                fontSize: '0.875rem',
                color: 'white',
                '& input': {
                    color: 'white',
                    '&::placeholder': {
                    color: 'rgba(255, 255, 255, 0.7)',
                    opacity: 1
                    }
                }
                }
            }}
            size="small"
            />
        </Paper>
        </Box>

        {/* Active Filters as a separate row below */}
        {(filters.customer_name || filters.room_type_id || filters.service_name || filters.payment_method || filters.dateFrom || filters.dateTo || filters.status || filters.check_in_from || filters.check_in_to) && (
        <Box sx={{
            display: 'flex',
            gap: 1,
            flexWrap: 'wrap',
            alignItems: 'center',
            mt: 2,
            pt: 2,
            borderTop: '1px solid',
            borderColor: 'divider'
        }}>
            <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
            Active filters:
            </Typography>
            {filters.customer_name && (
            <Chip
                label={`Customer: ${filters.customer_name}`}
                onDelete={() => setFilters(prev => ({ ...prev, customer_name: '' }))}
                color="primary"
                variant="outlined"
                size="small"
            />
            )}
            {filters.room_type_id && (
            <Chip
                label={`Room: ${getCategoriesNameById(filters.room_type_id, roomNamesData)}`}
                onDelete={() => setFilters(prev => ({ ...prev, room_type_id: '' }))}
                color="primary"
                variant="outlined"
                size="small"
            />
            )}
            {filters.service_name && (
            <Chip
                label={`Service: ${filters.service_name}`}
                onDelete={() => setFilters(prev => ({ ...prev, service_name: '' }))}
                color="primary"
                variant="outlined"
                size="small"
            />
            )}
            {filters.payment_method && (
            <Chip
                label={`Payment: ${getPaymentMode(filters.payment_method)}`}
                onDelete={() => setFilters(prev => ({ ...prev, payment_method: '' }))}
                color="primary"
                variant="outlined"
                size="small"
            />
            )}
            {filters.status && (
            <Chip
                label={`Status: ${getStatusById(filters.status)}`}
                onDelete={() => setFilters(prev => ({ ...prev, status: '' }))}
                color="primary"
                variant="outlined"
                size="small"
            />
            )}
            {filters.dateFrom && (
            <Chip
                label={`From: ${new Date(filters.dateFrom).toLocaleDateString()}`}
                onDelete={() => setFilters(prev => ({ ...prev, dateFrom: '' }))}
                color="primary"
                variant="outlined"
                size="small"
            />
            )}
            {filters.dateTo && (
            <Chip
                label={`To: ${new Date(filters.dateTo).toLocaleDateString()}`}
                onDelete={() => setFilters(prev => ({ ...prev, dateTo: '' }))}
                color="primary"
                variant="outlined"
                size="small"
            />
            )}
            {filters.check_in_from && (
            <Chip
                label={`Check-in From: ${new Date(filters.check_in_from).toLocaleDateString()}`}
                onDelete={() => setFilters(prev => ({ ...prev, check_in_from: '' }))}
                color="primary"
                variant="outlined"
                size="small"
            />
            )}
            {filters.check_in_to && (
            <Chip
                label={`Check-in To: ${new Date(filters.check_in_to).toLocaleDateString()}`}
                onDelete={() => setFilters(prev => ({ ...prev, check_in_to: '' }))}
                color="primary"
                variant="outlined"
                size="small"
            />
            )}
            <Button
            size="small"
            onClick={clearFilters}
            startIcon={<CloseIcon fontSize="small" />}
            sx={{ minWidth: 'auto' }}
            >
            Clear All
            </Button>
        </Box>
        )}
    </Paper>

    <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
    >
        <Paper elevation={2} sx={{ borderRadius: 3, overflow: 'hidden' }}>
        <TableContainer>
            <Table>
            <TableHead>
                <TableRow sx={{
                backgroundColor: theme.palette.primary.main,
                '& th': {
                    color: 'white',
                    fontWeight: 'bold',
                    fontSize: '0.95rem'
                }
                }}>
                <TableCell>#</TableCell>
                <TableCell>Number</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Customer</TableCell>
                <TableCell>Cost</TableCell>
                <TableCell>Qty</TableCell>
                <TableCell>Total</TableCell>
                <TableCell>Paid</TableCell>
                <TableCell>Balance</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="center">Actions</TableCell>
                </TableRow>
            </TableHead>
            <TableBody>
                {filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((booking, index) => (
                <TableRow
                    key={booking.id}
                    hover
                    sx={{
                    '&:nth-of-type(odd)': {
                        backgroundColor: theme.palette.action.hover
                    }
                    }}
                >
                    <TableCell>
                    <Chip
                        label={page * rowsPerPage + index + 1}
                        color="primary"
                        size="small"
                    />
                    </TableCell>
                    <TableCell>{booking.booking_no}</TableCell>
                    <TableCell>{formatCreatedAtDate(booking.created_at)}</TableCell>
                    <TableCell>{booking.customer_name || 'N/A'}</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>
                    {formatNumberWithComma(booking.cost)}
                    </TableCell>
                    <TableCell>
                    <Chip
                        label={booking.quantity}
                        color="info"
                        size="small"
                    />
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold', color: theme.palette.success.main }}>
                    {formatNumberWithComma(booking.total_amount)}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold', color: theme.palette.warning.main }}>
                    {formatNumberWithComma(booking.paid_amount)}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold', color: formatNumberWithComma(booking.balance_amount) > 0 ? theme.palette.error.main : theme.palette.success.main }}>
                    {formatNumberWithComma(booking.balance_amount)}
                    </TableCell>
                    <TableCell>
                    {booking.status === 1 ? (
                        <Chip
                        label={getStatusById(booking.status)}
                        size="small"
                        color="primary"
                        sx={{ mt: 0.5 }}
                        />
                    ) : (
                        <Chip
                        label={getStatusById(booking.status)}
                        size="small"
                        color="error"
                        sx={{ mt: 0.5 }}
                        />
                    )}
                    </TableCell>
                    <TableCell align="center">
                    <IconButton
                        edge="end"
                        onClick={() => handleOpenView(booking)}
                        color="primary"
                        sx={{
                        '&:hover': {
                            backgroundColor: theme.palette.info.light
                        }
                        }}
                    >
                        <ViewIcon />
                    </IconButton>
                    {hasPermission(PERMISSIONS.EDIT) && (<>
                        <IconButton
                        edge="end"
                        onClick={() => handleOpenEdit(booking)}
                        color="secondary"
                        sx={{
                            '&:hover': {
                            backgroundColor: theme.palette.success.light
                            }
                        }}
                        >
                        <EditIcon />
                        </IconButton>
                        </>)}
                        {hasPermission(PERMISSIONS.DELETE) && (<>
                        <IconButton
                        edge="end"
                        onClick={() => handleOpenDelete(booking)}
                        color="error"
                        sx={{
                            '&:hover': {
                            backgroundColor: theme.palette.error.light
                            }
                        }}
                        >
                        <Delete />
                        </IconButton>
                    </>)}
                    </TableCell>
                </TableRow>
                ))}
            </TableBody>
            </Table>
        </TableContainer>
        {/* Pagination */}
        <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50, 100]}
            component="div"
            count={filteredData.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{ mt: 2 }}
        />

        {filteredData.length === 0 && (
            <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            py: 8,
            color: 'text.secondary'
            }}>
            <Search sx={{ fontSize: 60, mb: 2 }} />
            <Typography variant="h6">No Hiring Place bookings found</Typography>
            <Typography variant="body2">Try adjusting your search query</Typography>
            </Box>
        )}
        </Paper>
    </motion.div>

    {/* Export Menu */}
    <Menu
        anchorEl={exportAnchorEl}
        open={Boolean(exportAnchorEl)}
        onClose={handleExportClose}
        anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
        }}
        transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
        }}
    >
        <MenuItem onClick={handleExportExcel} sx={{ gap: 1 }}>
        <TableChart fontSize="small" color="success" />
        Export to Excel (.xlsx)
        </MenuItem>
        <MenuItem onClick={handleExportPDF} sx={{ gap: 1 }}>
        <PictureAsPdf fontSize="small" color="error" />
        Export to PDF (.pdf)
        </MenuItem>
        <MenuItem onClick={handleExportCSV} sx={{ gap: 1 }}>
        <TableChart fontSize="small" color="info" />
        Export to CSV (.csv)
        </MenuItem>
    </Menu>

    {/* Options Dialog */}
    <Dialog
        open={filterOptions}
        onClose={() => setFilterOptions(false)}
        TransitionComponent={Transition}
        fullWidth
        maxWidth="md"
    >
        <DialogTitle sx={{ textAlign: 'center' }}>Filter Options</DialogTitle>
        <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, p: 2 }}>
            <Button
            variant="outlined"
            color="primary"
            startIcon={<RefreshIcon />}
            onClick={clearFilters}
            sx={{ textTransform: 'none' }}
            >
            Show All Bookings
            </Button>

            <Button
            variant="outlined"
            color="primary"
            startIcon={<TodayIcon />}
            onClick={applyTodayFilter}
            sx={{ textTransform: 'none' }}
            >
            Show Today's Bookings
            </Button>

            <Divider>OR</Divider>

            <Typography variant="subtitle1" sx={{ textAlign: 'center' }}>
            Filter by Customer Name
            </Typography>

            <TextField
            fullWidth
            label="Customer Name"
            name="customer_name"
            value={filters.customer_name}
            onChange={handleFilterChange}
            />

            <Divider>OR</Divider>

            <Typography variant="subtitle1" sx={{ textAlign: 'center' }}>
            Filter by Room Name
            </Typography>

            <FormControl fullWidth>
            <InputLabel>Room Name</InputLabel>
            <Select
                name="room_type_id"
                value={filters.room_type_id}
                onChange={handleFilterChange}
                label="Room Name"
            >
                <MenuItem value="">All Room Names</MenuItem>
                {roomNamesData.map((room) => (
                <MenuItem key={room.id} value={room.id}>
                    {room.roomtype}
                </MenuItem>
                ))}
            </Select>
            </FormControl>

            <Divider>OR</Divider>

            <Typography variant="subtitle1" sx={{ textAlign: 'center' }}>
            Filter by Service Name
            </Typography>

            <TextField
            fullWidth
            label="Service Name"
            name="service_name"
            value={filters.service_name}
            onChange={handleFilterChange}
            />

            <Divider>OR</Divider>

            <Typography variant="subtitle1" sx={{ textAlign: 'center' }}>
            Filter by Payment Method
            </Typography>

            <FormControl fullWidth>
            <InputLabel>Payment Method</InputLabel>
            <Select
                name="payment_method"
                value={filters.payment_method}
                onChange={handleFilterChange}
                label="Payment Method"
            >
                <MenuItem value="">All Payment Methods</MenuItem>
                {paymentModes.map((method) => (
                <MenuItem key={method.id} value={method.id}>
                    {method.name}
                </MenuItem>
                ))}
            </Select>
            </FormControl>

            <Divider>OR</Divider>

            <Typography variant="subtitle1" sx={{ textAlign: 'center' }}>
            Filter by Status
            </Typography>

            <FormControl fullWidth>
            <InputLabel>Status</InputLabel>
            <Select
                name="status"
                value={filters.status}
                onChange={handleFilterChange}
                label="Status"
            >
                {statusData.map((item, index) => (
                <MenuItem key={index} value={item.id}>{item.name}</MenuItem>
                ))}
            </Select>
            </FormControl>

            <Divider>OR</Divider>

            <Typography variant="subtitle1" sx={{ textAlign: 'center' }}>
            Filter by Booking Date Range
            </Typography>

            <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
                <TextField
                fullWidth
                label="From Date"
                type="date"
                name="dateFrom"
                value={filters.dateFrom}
                onChange={handleFilterChange}
                InputLabelProps={{ shrink: true }}
                />
            </Grid>
            <Grid item xs={12} sm={6}>
                <TextField
                fullWidth
                label="To Date"
                type="date"
                name="dateTo"
                value={filters.dateTo}
                onChange={handleFilterChange}
                InputLabelProps={{ shrink: true }}
                />
            </Grid>
            </Grid>

            <Divider>OR</Divider>

            <Typography variant="subtitle1" sx={{ textAlign: 'center' }}>
            Filter by Check-in Date Range
            </Typography>

            <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
                <TextField
                fullWidth
                label="Check-in From"
                type="date"
                name="check_in_from"
                value={filters.check_in_from}
                onChange={handleFilterChange}
                InputLabelProps={{ shrink: true }}
                />
            </Grid>
            <Grid item xs={12} sm={6}>
                <TextField
                fullWidth
                label="Check-in To"
                type="date"
                name="check_in_to"
                value={filters.check_in_to}
                onChange={handleFilterChange}
                InputLabelProps={{ shrink: true }}
                />
            </Grid>
            </Grid>
        </Box>
        </DialogContent>
        <DialogActions>
        <Button
            color="error"
            onClick={() => setFilterOptions(false)}
            variant="contained"
        >
            Close
        </Button>
        <Button
            onClick={() => setFilterOptions(false)}
            color="primary"
            variant="contained"
        >
            Apply Filters
        </Button>
        </DialogActions>
    </Dialog>

    {/* Add/Edit Dialog */}
    <Dialog
        open={openAdd}
        onClose={handleCloseAdd}
        TransitionComponent={Transition}
        fullWidth
        maxWidth="md"
        scroll="paper"
        sx={{
        '& .MuiDialog-paper': {
            maxHeight: '90vh',
            height: 'auto',
        }
        }}
    >
        <DialogTitle sx={{
        textAlign: 'center',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        pr: 1
        }}>
        <Typography variant="h6" sx={{ fontWeight: 'semibold', flex: 1 }}>
            {postStatus === "ADD" ? "New Hiring Place Booking" : "Edit Hiring Place Booking"}
        </Typography>
        <IconButton
            edge="end"
            color="inherit"
            onClick={handleCloseAdd}
            aria-label="close"
            size="small"
        >
            <X />
        </IconButton>
        </DialogTitle>
        <DialogContent dividers>
        <Box component="form" onSubmit={handleSubmit}>
            {/* Customer Details Card */}
            <Card sx={{ mb: 3 }}>
            <CardHeader 
                title="Customer Details" 
                avatar={<Person color="primary" />}
            />
            <CardContent>
                <Grid container spacing={2}>
                <Grid item xs={12}>
                    <TextField
                    fullWidth
                    label="Customer Name *"
                    name="customer_name"
                    value={formData.customer_name}
                    onChange={handleInputChange}
                    error={errors.customer_name}
                    helperText={errors.customer_name ? "Customer name is required" : "Enter customer name"}
                    required
                    InputProps={{
                        startAdornment: (
                        <InputAdornment position="start">
                            <Person />
                        </InputAdornment>
                        ),
                    }}
                    />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                    <TextField
                    fullWidth
                    label="Mobile No *"
                    name="customer_mobile"
                    value={formData.customer_mobile}
                    onChange={handleInputChange}
                    error={errors.customer_mobile}
                    helperText={errors.customer_mobile ? "Mobile number is required" : "10 digits"}
                    required
                    InputProps={{
                        startAdornment: (
                        <InputAdornment position="start">
                            <Phone />
                        </InputAdornment>
                        ),
                    }}
                    />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                    <TextField
                    fullWidth
                    label="Email"
                    name="customer_email"
                    type="email"
                    value={formData.customer_email}
                    onChange={handleInputChange}
                    InputProps={{
                        startAdornment: (
                        <InputAdornment position="start">
                            <Email />
                        </InputAdornment>
                        ),
                    }}
                    />
                </Grid>
                
                <Grid item xs={12}>
                    <TextField
                    fullWidth
                    label="Address"
                    name="customer_address"
                    value={formData.customer_address}
                    onChange={handleInputChange}
                    multiline
                    rows={2}
                    InputProps={{
                        startAdornment: (
                        <InputAdornment position="start">
                            <LocationOn />
                        </InputAdornment>
                        ),
                    }}
                    />
                </Grid>
                </Grid>
            </CardContent>
            </Card>

            {/* Room Details Card */}
            <Card sx={{ mb: 3 }}>
            <CardHeader 
                title="Room Details" 
                avatar={<Hotel color="primary" />}
            />
            <CardContent>
                <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                    <FormControl fullWidth error={errors.room_type_id}>
                    <InputLabel>Room Name *</InputLabel>
                    <Select
                        name="room_type_id"
                        value={formData.room_type_id}
                        onChange={handleInputChange}
                        label="Room Name *"
                        required
                    >
                        <MenuItem value="">
                        <em>Select Room Name</em>
                        </MenuItem>
                        {roomNamesData.map((room) => (
                        <MenuItem key={room.id} value={room.id}>
                            {room.roomtype}
                        </MenuItem>
                        ))}
                    </Select>
                    {errors.room_type_id && (
                        <Typography variant="caption" color="error">
                        Room Name is required
                        </Typography>
                    )}
                    </FormControl>
                </Grid>

                <Grid item xs={12}>
                    <TextField
                    fullWidth
                    label="Service Name"
                    name="service_name"
                    value={formData.service_name}
                    onChange={handleInputChange}
                    multiline
                    rows={2}
                    />
                </Grid>
                </Grid>
            </CardContent>
            </Card>

            {/* Booking Details Card */}
            <Card sx={{ mb: 3 }}>
            <CardHeader 
                title="Booking Details" 
                avatar={<MeetingRoom color="primary" />}
            />
            <CardContent>
                <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                    <TextField
                    fullWidth
                    label="Check-in Date"
                    name="check_in_date"
                    type="date"
                    value={formData.check_in_date}
                    onChange={handleInputChange}
                    InputLabelProps={{ shrink: true }}
                    InputProps={{
                        startAdornment: (
                        <InputAdornment position="start">
                            <TodayIcon />
                        </InputAdornment>
                        ),
                    }}
                    />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                    <TextField
                    fullWidth
                    label="Check-out Date"
                    name="check_out_date"
                    type="date"
                    value={formData.check_out_date}
                    onChange={handleInputChange}
                    InputLabelProps={{ shrink: true }}
                    InputProps={{
                        startAdornment: (
                        <InputAdornment position="start">
                            <TodayIcon />
                        </InputAdornment>
                        ),
                    }}
                    />
                </Grid>
                
                <Grid item xs={12}>
                    <TextField
                    fullWidth
                    label="Description"
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    multiline
                    rows={3}
                    placeholder="Additional notes or special requests"
                    />
                </Grid>
                </Grid>
            </CardContent>
            </Card>

            {/* Payment Details Card */}
            <Card sx={{ mb: 3 }}>
            <CardHeader 
                title="Payment Details" 
                avatar={<Payments color="primary" />}
            />
            <CardContent>
                <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                    <TextField
                    fullWidth
                    label="Cost per Unit *"
                    name="cost"
                    type="number"
                    value={formData.cost}
                    onChange={handleInputChange}
                    error={errors.cost}
                    helperText={errors.cost ? "Valid cost is required" : ""}
                    required
                    inputProps={{ min: 0, step: 0.01 }}
                    InputProps={{
                        startAdornment: (
                        <InputAdornment position="start">
                            <AttachMoney />
                        </InputAdornment>
                        ),
                    }}
                    />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                    <TextField
                    fullWidth
                    label="Quantity *"
                    name="quantity"
                    type="number"
                    value={formData.quantity}
                    onChange={handleInputChange}
                    error={errors.quantity}
                    helperText={errors.quantity ? "Valid quantity is required" : ""}
                    required
                    inputProps={{ min: 1 }}
                    InputProps={{
                        startAdornment: (
                        <InputAdornment position="start">
                            <MeetingRoom />
                        </InputAdornment>
                        ),
                    }}
                    />
                </Grid>
                
                <Grid item xs={12} sm={4}>
                    <TextField
                    fullWidth
                    label="Total Amount *"
                    name="total_amount"
                    type="number"
                    value={formData.total_amount}
                    onChange={handleInputChange}
                    error={errors.total_amount}
                    helperText={errors.total_amount ? "Valid total amount is required" : ""}
                    required
                    inputProps={{ min: 0, step: 0.01, readOnly: true }}
                    InputProps={{
                        startAdornment: (
                        <InputAdornment position="start">
                            <AttachMoney />
                        </InputAdornment>
                        ),
                    }}
                    />
                </Grid>
                
                <Grid item xs={12} sm={4}>
                    <TextField
                    fullWidth
                    label="Paid Amount *"
                    name="paid_amount"
                    type="number"
                    value={formData.paid_amount}
                    onChange={handleInputChange}
                    error={errors.paid_amount}
                    helperText={errors.paid_amount ? "Valid paid amount is required" : ""}
                    required
                    inputProps={{ min: 0, step: 0.01 }}
                    InputProps={{
                        startAdornment: (
                        <InputAdornment position="start">
                            <AttachMoney />
                        </InputAdornment>
                        ),
                    }}
                    />
                </Grid>
                
                <Grid item xs={12} sm={4}>
                    <TextField
                    fullWidth
                    label="Balance Amount *"
                    name="balance_amount"
                    type="number"
                    value={formData.balance_amount}
                    onChange={handleInputChange}
                    error={errors.balance_amount}
                    helperText={errors.balance_amount ? "Valid balance amount is required" : ""}
                    required
                    inputProps={{ min: 0, step: 0.01, readOnly: true }}
                    InputProps={{
                        startAdornment: (
                        <InputAdornment position="start">
                            <AttachMoney />
                        </InputAdornment>
                        ),
                    }}
                    />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                    <FormControl fullWidth error={errors.payment_method}>
                    <InputLabel>Payment Method *</InputLabel>
                    <Select
                        name="payment_method"
                        value={formData.payment_method}
                        onChange={handleInputChange}
                        label="Payment Method *"
                        required
                    >
                        <MenuItem value="">
                        <em>Select Payment Method</em>
                        </MenuItem>
                        {paymentModes.map((method) => (
                        <MenuItem key={method.id} value={method.id}>
                            {method.name}
                        </MenuItem>
                        ))}
                    </Select>
                    {errors.payment_method && (
                        <Typography variant="caption" color="error">
                        Payment method is required
                        </Typography>
                    )}
                    </FormControl>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                    <InputLabel>Status</InputLabel>
                    <Select
                        name="status"
                        value={formData.status}
                        onChange={handleInputChange}
                        label="Status"
                    >
                        {statusData.map((item, index) => (
                        <MenuItem key={index} value={item.id}>{item.name}</MenuItem>
                        ))}
                    </Select>
                    </FormControl>
                </Grid>
                </Grid>
            </CardContent>
            </Card>
        </Box>
        </DialogContent>
        <DialogActions sx={{
        px: 3,
        py: 2,
        justifyContent: 'space-between'
        }}>
        <Box>
            <Button
            color="error"
            onClick={handleCloseAdd}
            variant="contained"
            sx={{ mr: 1 }}
            >
            Cancel
            </Button>
        </Box>
        <Box>
            <Button
            type="submit"
            color="primary"
            variant="contained"
            onClick={handleSubmit}
            >
            {postStatus === "ADD" ? "Create Booking" : "Update Booking"}
            </Button>
        </Box>
        </DialogActions>
    </Dialog>

    {/* View Dialog */}
    <Dialog
        open={openView}
        onClose={handleCloseView}
        TransitionComponent={Transition}
        fullWidth
        maxWidth="sm"
    >
        <DialogTitle sx={{ textAlign: 'center' }}><b>Hiring Place Booking Details</b></DialogTitle>
        <DialogContent>
        {selectedRecord && (
            <Box sx={{ p: 2 }}>
            <Grid container spacing={3}>
                <Grid item xs={12}>
                <Typography variant="subtitle2"><b>Booking ID:</b></Typography>
                <Typography variant="h6" sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
                    #{selectedRecord.booking_no}
                </Typography>
                </Grid>
                
                <Grid item xs={12}>
                <Typography variant="subtitle2"><b>Customer:</b></Typography>
                <Box sx={{ mt: 1, p: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}>
                    <Typography variant="body1" fontWeight="bold">{selectedRecord.customer_name || 'N/A'}</Typography>
                    {selectedRecord.customer_phone && (
                    <Typography variant="body2">📞 {selectedRecord.customer_phone}</Typography>
                    )}
                    {selectedRecord.customer_email && (
                    <Typography variant="body2">✉️ {selectedRecord.customer_email}</Typography>
                    )}
                    {selectedRecord.customer_address && (
                    <Typography variant="body2">📍 {selectedRecord.customer_address}</Typography>
                    )}
                </Box>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2"><b>Room Name:</b></Typography>
                <Typography variant="body1">{selectedRecord.room_type_name || 'N/A'}</Typography>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2"><b>Service Name:</b></Typography>
                <Typography variant="body1">{selectedRecord.service_name || 'N/A'}</Typography>
                </Grid>
                
                <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>Payment Details</Typography>
                <Grid container spacing={2}>
                    <Grid item xs={6} sm={3}>
                    <Typography variant="subtitle2"><b>Cost:</b></Typography>
                    <Typography variant="body1" fontWeight="bold" color="primary">
                        {formatNumberWithComma(selectedRecord.cost)}
                    </Typography>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                    <Typography variant="subtitle2"><b>Quantity:</b></Typography>
                    <Typography variant="body1">{formatNumberWithComma(selectedRecord.quantity)}</Typography>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                    <Typography variant="subtitle2"><b>Total:</b></Typography>
                    <Typography variant="body1" fontWeight="bold" color="success.main">
                        {formatNumberWithComma(selectedRecord.total_amount)}
                    </Typography>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                    <Typography variant="subtitle2"><b>Paid:</b></Typography>
                    <Typography variant="body1" fontWeight="bold" color="warning.main">
                        {formatNumberWithComma(selectedRecord.paid_amount)}
                    </Typography>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                    <Typography variant="subtitle2"><b>Balance:</b></Typography>
                    <Typography variant="body1" fontWeight="bold" color={parseFloat(selectedRecord.balance_amount) > 0 ? "error.main" : "success.main"}>
                        {formatNumberWithComma(selectedRecord.balance_amount)}
                    </Typography>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                    <Typography variant="subtitle2"><b>Payment:</b></Typography>
                    <Chip
                        label={getPaymentMode(selectedRecord.payment_method)}
                        color="secondary"
                        size="small"
                        variant="outlined"
                    />
                    </Grid>
                </Grid>
                </Grid>
                
                {selectedRecord.check_in_date && (
                <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2"><b>Check-in Date:</b></Typography>
                    <Typography variant="body1">{selectedRecord.check_in_date}</Typography>
                </Grid>
                )}
                
                {selectedRecord.check_out_date && (
                <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2"><b>Check-out Date:</b></Typography>
                    <Typography variant="body1">{selectedRecord.check_out_date}</Typography>
                </Grid>
                )}
                
                <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2"><b>Status:</b></Typography>
                {selectedRecord.status === 1 ? (
                    <Chip
                    label={getStatusById(selectedRecord.status)}
                    size="small"
                    color="primary"
                    sx={{ mt: 0.5 }}
                    />
                ) : (
                    <Chip
                    label={getStatusById(selectedRecord.status)}
                    size="small"
                    color="error"
                    sx={{ mt: 0.5 }}
                    />
                )}
                </Grid>
                
                <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2"><b>Created Date:</b></Typography>
                <Typography variant="body1">{formatCreatedAtDate(selectedRecord.created_at)}</Typography>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2"><b>Last Updated:</b></Typography>
                <Typography variant="body1">{formatCreatedAtDate(selectedRecord.updated_at)}</Typography>
                </Grid>
            </Grid>
            <br/>
            {selectedRecord.description && (
                <Box>
                    <Typography variant="subtitle2"><b>Description:</b></Typography>
                    <Typography variant="body1" sx={{ mt: 1, p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
                    {selectedRecord.description}
                    </Typography>
                </Box>
            )}
            </Box>
        )}
        </DialogContent>
        <DialogActions>
        <Button
            color="error"
            onClick={handleCloseView}
            variant="contained"
        >
            Close
        </Button>
        </DialogActions>
    </Dialog>

    {/* Delete Confirmation Dialog */}
    <Dialog
        open={openDelete}
        onClose={handleCloseDelete}
        TransitionComponent={Transition}
    >
        <DialogTitle sx={{ textAlign: 'center' }}>Delete Hiring Place Booking</DialogTitle>
        <DialogContent>
        <Typography variant="body1" sx={{ textAlign: 'center', mb: 2 }}>
            Are you sure you want to delete this Hiring Place booking?
        </Typography>
        {selectedRecord && (
            <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                Booking #{selectedRecord.id}
            </Typography>
            <Typography variant="body2" color="text.secondary">
                Customer: {selectedRecord.customer_name}
            </Typography>
            <Typography variant="body2" color="text.secondary">
                Room: {selectedRecord.room_type_name}
            </Typography>
            <Typography variant="body2" color="text.secondary">
                Total Amount: {formatNumberWithComma(selectedRecord.total_amount)}
            </Typography>
            </Box>
        )}
        </DialogContent>
        <DialogActions>
        <Button
            color="error"
            onClick={handleCloseDelete}
            variant="contained"
        >
            Cancel
        </Button>
        <Button
            onClick={handleSubmit}
            color="primary"
            variant="contained"
        >
            Confirm Delete
        </Button>
        </DialogActions>
    </Dialog>
    </Container>
);
};

export default HiringPlace;

const Transition = React.forwardRef(function Transition(props, ref) {
return <Slide direction="up" ref={ref} {...props} />;
});